import React, { useEffect, lazy, Suspense } from 'react'
import { colors } from '@fs/zion-ui'
import { css } from '@linaria/core'
import { useFeatureFlag } from '@fs/zion-flags'
import { useUser } from '@fs/zion-user'
import { useAdobeTarget } from '@fs/zion-adobe-target'
import { useCache } from '@fs/zion-cache'
import { PreviousUserProvider, usePreviousUser } from '@fs/zion-previous-user-service'
import AlertBanner from '@fs/zion-ui/alert-banner'
import Header2019WithData, { Header2019 } from './Header2019'
import AdminHeader, { AdminHeaderButton, AdminHeaderIconButton } from './AdminHeader'
import CFHeader from './CFHeader/CFHeader'
import LogoOnlyHeader from './LogoOnlyHeader'
import './locales'

const EXPERIENCES = {
  HEADER_NEW_LOGGED_OUT: 'NewHeaderLoggedOut',
  HEADER_OLD_LOGGED_OUT: 'OldHeaderLoggedOut',
}
export const homeHeaderActivityConfig = {
  location: 'home_header_unauthenticated',
  experienceIds: Object.values(EXPERIENCES),
}
const getHeaderAdobeConfig = (experienceId) => {
  switch (experienceId) {
    case EXPERIENCES.HEADER_NEW_LOGGED_OUT:
      return true
    case EXPERIENCES.HEADER_OLD_LOGGED_OUT:
      return false
    default:
      return false
  }
}

const UnauthenticatedHeader = lazy(() => import('./UnauthenticatedHeader'))

const PlaceholderHeaderCss = css`
  height: 60px;
  background: ${colors.gray00};
`
const cache = 'header-unauthenticated'
const key = 'show_unauthenticated_header'

const headerContainerCss = css`
  border-bottom: 1px solid var(--gray10);
`

function PlaceholderHeader() {
  return <div className={PlaceholderHeaderCss} />
}

function useSafeAdobeTarget(config) {
  try {
    return useAdobeTarget(config)
  } catch (error) {
    return { data: null, loading: false, error: true }
  }
}

/**
 * Wrapper component that adds AlertBanner to any header.
 */
function HeaderWithAlertBanner({ children, config }) {
  return (
    <div className={headerContainerCss}>
      {children}
      {config?.alertBannerProps && <AlertBanner {...config.alertBannerProps} />}
    </div>
  )
}

function UnauthenticatedHeaderBase(props) {
  // Handle both Root's <Header config={...} /> and direct <Header showSubHeader={true} />
  const { config = props } = props
  const { signedIn, userLoading } = useUser()

  if (userLoading) {
    return <PlaceholderHeader />
  }

  if (signedIn) {
    return <Header2019WithData {...props} subHeaderConfig={config} />
  }

  return (
    <Suspense fallback={<PlaceholderHeader />}>
      <UnauthenticatedHeader {...props} />
    </Suspense>
  )
}
function PreviousAndAdobeHeader(props) {
  const { config = props } = props
  const { data: adobeData, loading: adobeLoading, error: adobeError } = useSafeAdobeTarget(homeHeaderActivityConfig)
  const previousUser = usePreviousUser()
  const [showUnauthHeader, setShowUnauthHeader] = useCache({
    cache,
    key,
  })

  useEffect(() => {
    if (showUnauthHeader === null) {
      setShowUnauthHeader(previousUser.newUser === false ? 'false' : 'true')
    }
  }, [setShowUnauthHeader, showUnauthHeader, previousUser.newUser])

  if (adobeError) {
    return <Header2019WithData {...props} />
  }

  if (showUnauthHeader === undefined || adobeLoading) {
    return <PlaceholderHeader />
  }

  if (getHeaderAdobeConfig(adobeData?.experienceId) && showUnauthHeader === 'true') {
    return <UnauthenticatedHeaderBase {...props} />
  }

  return <Header2019WithData {...props} subHeaderConfig={config} />
}
function HeaderAdobeABTest(props) {
  const { config = props } = props
  const { data: adobeData, loading: adobeLoading, error: adobeError } = useSafeAdobeTarget(homeHeaderActivityConfig)

  if (adobeError) {
    return <Header2019WithData {...props} />
  }

  if (adobeLoading) {
    return <PlaceholderHeader />
  }
  if (!getHeaderAdobeConfig(adobeData?.experienceId)) {
    return <Header2019WithData {...props} subHeaderConfig={config} />
  }
  return <UnauthenticatedHeaderBase {...props} />
}

function DefaultHeaderPreviousUser(props) {
  const { config = props } = props
  const previousUser = usePreviousUser()
  const [showUnauthHeader, setShowUnauthHeader] = useCache({
    cache,
    key,
  })

  useEffect(() => {
    if (showUnauthHeader === null) {
      setShowUnauthHeader(previousUser.newUser === false ? 'false' : 'true')
    }
  }, [setShowUnauthHeader, showUnauthHeader, previousUser.newUser])

  if (showUnauthHeader === undefined) {
    return <PlaceholderHeader />
  }
  if (showUnauthHeader === 'true') {
    return <UnauthenticatedHeaderBase {...props} />
  }
  return <Header2019WithData {...props} subHeaderConfig={config} />
}

function DefaultHeader(props) {
  const headerFlag = useFeatureFlag('home_header_unauthenticatedHeader')
  // Handle both Root's <Header config={...} /> and direct <Header showSubHeader={true} />
  const { config = props } = props

  let content
  if (headerFlag.isOn) {
    const unauthenticatedHeaderOffPreviousUserFlag = headerFlag?.treatment === 'previous-user-tracking-only'
    const previousUserFlag = headerFlag?.treatment === 'previous-user'
    const adobeABTestFlag = headerFlag?.treatment === 'adobe'
    const adobeAndPreviousUserFlag = headerFlag?.treatment === 'previous-user-adobe'

    if (unauthenticatedHeaderOffPreviousUserFlag) {
      content = (
        <PreviousUserProvider>
          <Header2019WithData {...props} subHeaderConfig={config} />
        </PreviousUserProvider>
      )
    } else if (adobeAndPreviousUserFlag) {
      content = (
        <PreviousUserProvider>
          <PreviousAndAdobeHeader {...props} />
        </PreviousUserProvider>
      )
    } else if (adobeABTestFlag) {
      content = <HeaderAdobeABTest {...props} />
    } else if (previousUserFlag) {
      content = (
        <PreviousUserProvider>
          <DefaultHeaderPreviousUser {...props} />
        </PreviousUserProvider>
      )
    } else {
      content = <UnauthenticatedHeaderBase {...props} />
    }
  } else {
    content = <Header2019WithData {...props} subHeaderConfig={config} />
  }

  return <HeaderWithAlertBanner config={config}>{content}</HeaderWithAlertBanner>
}

export default DefaultHeader
export {
  Header2019,
  AdminHeader,
  AdminHeaderIconButton,
  AdminHeaderButton,
  CFHeader,
  LogoOnlyHeader,
  UnauthenticatedHeader,
}
